'use client';

import React, { useContext, FC, useMemo } from 'react';
import { observer } from 'mobx-react';
import { RootStoreContext } from '@stores/rootStore';
import { LFSelect, LFFilterLabelDropdown, ILabelOption } from '@app/_components/form-control/selects';
import { WaitForLoad } from '@components/spinner/Spinner';
import LabelLoader from './LabelLoader';
import { useActiveCrop, useActiveLabel, useActiveCountry, useActiveDistrict, useActiveProvince, useActiveSubdistrict, useActiveVillage } from './hooks';
import { isPresent } from '@farmai-shared/LFUtils';
import { sendGaEvent } from '@services/utils';
import { InfoCircle } from '@components/icons';
import { useTranslation } from 'react-i18next';
import { SeasonYearFilterDropdown } from '@app/_components/common/SeasonYearFilterDropdown';
const cropOptions: {
  label: string;
  value: string | null | undefined;
}[] = [{
  label: 'All',
  value: undefined
}];
export const toLabelOption = (l): ILabelOption => ({
  label: l.name,
  value: l.id,
  color: l.color
});
export const OverviewFilterDropdowns: FC = observer(() => {
  const {
    uiStore,
    userStore
  } = useContext(RootStoreContext);
  const {
    activeCrop,
    activeCropLabel,
    crops,
    isLoading
  } = useActiveCrop();
  const {
    t
  } = useTranslation();
  const {
    countries,
    activeCountry,
    activeCountryLabel,
    isLoading: isLoadingCountries
  } = useActiveCountry();
  const {
    provinces,
    activeProvince,
    activeProvinceLabel,
    isLoading: isLoadingProvinces
  } = useActiveProvince();
  const {
    districts,
    activeDistrict,
    activeDistrictLabel,
    isLoading: isLoadingDistricts
  } = useActiveDistrict();
  const {
    subdistricts,
    activeSubdistrict,
    activeSubdistrictLabel,
    isLoading: isLoadingSubdistricts
  } = useActiveSubdistrict();
  const {
    villages,
    activeVillage,
    activeVillageLabel,
    isLoading: isLoadingVillages
  } = useActiveVillage();
  const {
    labelOptions,
    isLoading: isLoadingLabels,
    handleLabelChange
  } = useActiveLabel();
  const lang = userStore.user.settings.lang;
  const loadLabelOptions = async (search, prevOptions, {
    page
  }) => {
    const labels = (await uiStore.currentOrganization?.fetchLabels({
      page,
      search
    })) || [];
    const hasMore = labels && labels.length > 0;
    return {
      options: labels.map(toLabelOption),
      hasMore: hasMore,
      additional: {
        page: page + 1
      }
    };
  };

  // used for GA reporting only. Actual stage change managed by query params
  const handleSelect = (filter: string) => sendGaEvent('select_overview_filter', {
    event_category: 'overview',
    filter: filter,
    action: 'click'
  });
  return <div className={`
        flex w-full flex-col px-12 pt-0
        print:hidden
      `}>
      <div className={`flex flex-col items-start gap-x-8 gap-y-5`}>
        <LFSelect key={activeCrop?.name || 'crop_name'} label="crop" options={cropOptions.concat(crops?.map(crop => ({
        label: crop.name_i18n[lang],
        value: crop.name
      })))} isLoading={isLoading} withDefault queryParamKey="filter[crop]" testId="overview-crop-filter" defaultValue={activeCrop ? {
        label: activeCropLabel,
        value: activeCrop.name
      } : undefined} textTransform="capitalize" onChange={() => handleSelect('crop')} />

        {countries.length > 1 && <LFSelect label="country" options={countries.map(c => ({
        label: c[lang] || c['en'] || c['native'],
        value: c['en'] || c['native']
      }))} isLoading={isLoadingCountries} withDefault queryParamKey="filter[country]" textTransform="capitalize" defaultValue={activeCountry ? {
        label: activeCountryLabel,
        value: activeCountry.en || activeCountry.native
      } : undefined} onChange={() => handleSelect('country')} />}

        {(isPresent(activeCountry) || countries.length <= 1) && <LFSelect label="province" options={provinces.map(c => ({
        label: c[lang] || c['en'] || c['native'],
        value: c['en'] || c['native']
      }))} isLoading={isLoadingProvinces} withDefault queryParamKey="filter[province]" textTransform="capitalize" defaultValue={activeProvince ? {
        label: activeProvinceLabel,
        value: activeProvince.en || activeProvince.native
      } : undefined} onChange={() => handleSelect('province')} />}

        {isPresent(activeProvince) && <LFSelect label="district" options={districts.map(c => ({
        label: c[lang] || c['en'] || c['native'],
        value: c['en'] || c['native']
      }))} isLoading={isLoadingDistricts} withDefault queryParamKey="filter[district]" textTransform="capitalize" defaultValue={activeDistrict ? {
        label: activeDistrictLabel,
        value: activeDistrict.en || activeDistrict.native
      } : undefined} onChange={() => handleSelect('district')} />}

        {isPresent(activeDistrict) && <LFSelect label="subdistrict" options={subdistricts.map(c => ({
        label: c[lang] || c['en'] || c['native'],
        value: c['en'] || c['native']
      }))} isLoading={isLoadingSubdistricts} withDefault queryParamKey="filter[subdistrict]" textTransform="capitalize" defaultValue={activeSubdistrict ? {
        label: activeSubdistrictLabel,
        value: activeSubdistrict.en || activeSubdistrict.native
      } : undefined} onChange={() => handleSelect('subdistrict')} />}

        {isPresent(activeSubdistrict) && <div className="flex w-full flex-col gap-y-1">
            <div data-tooltip-id="main-tooltip" data-tooltip-content={t('villageLabelsTooltip')} className={`
                hidden w-fit
                md:flex
              `}>
              <InfoCircle height={12} width={12} stroke="#727D9F" fill="#fff" />
            </div>
            <div data-tooltip-id="mobile-enabled-tooltip" data-tooltip-content={t('villageLabelsTooltip')} className={`
                mt-auto mb-0 flex w-fit
                md:hidden
              `}>
              <InfoCircle height={12} width={12} stroke="#727D9F" fill="#fff" />
            </div>
            <LFSelect label="village" options={villages.map(c => ({
          label: c[lang] || c['en'] || c['native'],
          value: c['en'] || c['native']
        }))} isLoading={isLoadingVillages} withDefault queryParamKey="filter[village]" textTransform="capitalize" defaultValue={activeVillage ? {
          label: activeVillageLabel,
          value: activeVillage.en || activeVillage.native
        } : undefined} onChange={() => handleSelect('village')} />
          </div>}

        <SeasonYearFilterDropdown />
      </div>

      <div className={`
          mt-2
          print:hidden
        `}>
        <WaitForLoad isLoading={isLoadingLabels} customSpinner={<LabelLoader />}>
          {() => <LFFilterLabelDropdown value={labelOptions} loadOptions={loadLabelOptions} onChange={() => {
          handleSelect('labels');
          handleLabelChange;
        }} customQueryKey="filter[label_ids][]" />}
        </WaitForLoad>
      </div>
    </div>;
});